import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { IoLockClosedOutline, IoPersonOutline } from "react-icons/io5";
import { useNotify } from "../components/ui/NotificationProvider";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../api/unwrapApiResponse";
import logo from "../assets/images/logomark.png";

export default function CmsLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const { isAuthenticated, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || "/cmsadmin/dashboard/pages";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim(), password);
      notify.success("Signed in");
      const from = location.state?.from?.pathname || "/cmsadmin/dashboard/pages";
      navigate(from, { replace: true });
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-vucali-bg px-4 py-12">
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #02DF82 0%, transparent 45%), radial-gradient(circle at 80% 80%, #016A3E 0%, transparent 40%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <img src={logo} alt="Logo" className="mx-auto h-12 w-auto" />
          <h1 className="mt-2 font-display text-2xl text-white md:text-3xl">
            Content admin
          </h1>
          <p className="mt-2 text-sm text-white/50">
            Internal access only — not linked from the public site.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/10 bg-vucali-surface/80 p-8 shadow-[0_0_40px_rgba(2,223,130,0.06)] backdrop-blur-md"
        >
          <label className="block text-sm font-medium text-white/80">
            <span className="mb-2 flex items-center gap-2">
              <IoPersonOutline className="text-vucali-primary" aria-hidden />
              Username
            </span>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-vucali-bg/80 px-4 py-3 text-white outline-none ring-vucali-primary/40 transition placeholder:text-white/30 focus:border-vucali-primary/50 focus:ring-2"
              placeholder="Your username"
            />
          </label>

          <label className="mt-5 block text-sm font-medium text-white/80">
            <span className="mb-2 flex items-center gap-2">
              <IoLockClosedOutline className="text-vucali-primary" aria-hidden />
              Password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-vucali-bg/80 px-4 py-3 text-white outline-none ring-vucali-primary/40 transition placeholder:text-white/30 focus:border-vucali-primary/50 focus:ring-2"
              placeholder="••••••••"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            className="mt-8 w-full rounded-xl bg-vucali-primary py-3.5 font-display text-sm font-semibold uppercase tracking-wide text-vucali-bg transition hover:bg-vucali-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
